import './common/assets/main.css';

import { plugin } from '@formkit/vue';
import * as Sentry from '@sentry/vue';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import Badge from '@/common/components/ui/badge/badge.component.vue';
import Box from '@/common/components/ui/box/box.component.vue';
import Button from '@/common/components/ui/button/button.component.vue';
import Card from '@/common/components/ui/card/card.component.vue';
import Container from '@/common/components/ui/container/container.component.vue';
import Tag from '@/common/components/ui/tag/tag.component.vue';
import App from './app.view.vue';
import { formatDate, formatNumber } from './common/helpers';
import { i18n } from './common/plugins/i18n.plugin';
import ToastPlugin, { type ToastService } from './common/plugins/toast.plugin';
import { ApiService } from './common/services/api.service-new';
import formkitConfig from './formkit.config';
import router from './router';
import { useAuthStore } from './views/auth/auth.store';

dayjs.extend(minMax);
dayjs.extend(utc);

declare module 'vue' {
  interface ComponentCustomProperties {
    $toast: ToastService;
    $formatNumber: typeof formatNumber;
    $formatDate: typeof formatDate;
  }
}

// Lazy-route failures are wrapped differently across browsers and, in some
// vue-router versions, by a secondary component-resolution error. They all
// represent the same stale-deploy recovery path below.
const STALE_CHUNK_ERROR_RE = /dynamically imported module|Importing a module script failed|Unable to preload (CSS|module)|Couldn't resolve component "default" at/i;

async function bootstrap(): Promise<void> {
  // Local-only traffic + main-thread monitor. Imported DYNAMICALLY behind a
  // statically-foldable `import.meta.env.DEV`, so the module and its `fetch`
  // patch are absent from any production bundle rather than merely inert in
  // one. It self-gates again on hostname + opt-in env var at runtime; see
  // `common/dev/request-monitor`. Deliberately not awaited — bootstrap must not
  // wait on a diagnostic.
  if (import.meta.env.DEV) {
    // Detached on purpose — bootstrap must not wait on a diagnostic. The cost
    // is that traffic in the first tick (a cold deep link) goes unrecorded,
    // which the monitor's own docs state. A rejection here (a transient Vite
    // chunk failure) must not surface as an unhandled rejection in app error
    // handling: the diagnostic is optional, so it fails quietly.
    void import('@/common/dev/request-monitor')
      .then(({ installRequestMonitor }) => {
        installRequestMonitor();
      })
      .catch(() => {
        // Swallowed deliberately: the monitor is optional, and a failed
        // diagnostic must not surface as an unhandled rejection in app error
        // handling. Its absence is visible by `trafficReport` being undefined.
      });
  }

  const app = createApp(App);

  // Registered BEFORE Sentry.init on purpose. `Sentry.init({ app })` wraps
  // `app.config.errorHandler`, capturing whatever is already there as the
  // "original" and — when there is nothing — *rethrowing* the error instead
  // (see @sentry/vue `attachErrorHandler`). A throw out of the error handler
  // escapes Vue's `handleError` and aborts the in-progress patch, which leaves
  // a half-patched tree whose next patch dies on a null component ("cannot
  // access property emitsOptions"). That turns any single recoverable
  // component error into a dead subtree.
  //
  // Vue's own default is to log and continue, so this restores that while
  // keeping Sentry reporting: Sentry captures the exception, then calls this.
  // Registering it *after* init would instead replace Sentry's wrapper and
  // silently disable Vue error reporting.
  // Logged at `warn`, not `error`, on purpose: `captureConsoleIntegration`
  // below hooks `console.error`, so logging at error level would report every
  // Vue error twice — once as the exception Sentry captures here, once as a
  // console event. `warn` keeps the line visible in devtools without the
  // duplicate. Severity is honest: with a handler registered these errors are
  // handled and no longer fatal.
  app.config.errorHandler = (error, _instance, info): void => {
    console.warn(`[vue] handled component error (${info}):`, error);
  };

  const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

  if (import.meta.env.VITE_SENTRY_DSN && !isLocalhost) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_STAGE || 'unknown',
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
        Sentry.extraErrorDataIntegration(),
      ],
      // OL's Google basemap source awaits tile/attribution fetches without
      // a catch, so flaky-network failures surface as unhandled rejections.
      // Not actionable on our side; the host suffix is appended by Sentry's
      // fetch instrumentation, making this match safely narrow.
      //
      // The dynamic-import / preload patterns are post-deploy stale-chunk
      // failures that the `vite:preloadError` / `router.onError` handlers
      // below auto-recover from by reloading, so they're not actionable.
      ignoreErrors: [
        /\(tile\.googleapis\.com\)/,
        // Browser dynamic-import failure messages differ, and vue-router may
        // wrap the failed import as "Couldn't resolve component default".
        // All are recovered by the one-shot router handler below.
        STALE_CHUNK_ERROR_RE,
      ],
      attachStacktrace: true,
      tracesSampleRate: import.meta.env.DEV
        ? 0
        : import.meta.env.VITE_STAGE === 'production'
          ? 0.05
          : 0.2,
      tracePropagationTargets: [],
    });
  }

  app.config.globalProperties.$formatNumber = formatNumber;
  app.config.globalProperties.$formatDate = formatDate;

  app.use(plugin, formkitConfig);
  app.use(i18n);
  app.use(ToastPlugin);
  app.use(createPinia());

  const authStore = useAuthStore();

  app.provide('authStore', authStore);
  app.provide('api', new ApiService(authStore));

  try {
    await authStore.autoLogin();
  } catch {
    // Session expired or invalid — app will redirect to login via router guards
  }

  app.component('Badge', Badge);
  app.component('Box', Box);
  app.component('AppButton', Button);
  app.component('Card', Card);
  app.component('Container', Container);
  app.component('Tag', Tag);

  app.use(router);

  // Stale-chunk recovery: after a deploy, an old index.html may request
  // content-hashed chunks that no longer exist. Reload once to pick up the
  // fresh index; a sessionStorage guard prevents reload loops if the asset
  // is genuinely gone. Cleared on the next successful navigation so a later
  // deploy can recover again.
  const STALE_CHUNK_KEY = 'stale-chunk-reload-attempted';
  const recoverFromStaleChunk = (): void => {
    try {
      if (sessionStorage.getItem(STALE_CHUNK_KEY)) {
        return;
      }
      sessionStorage.setItem(STALE_CHUNK_KEY, '1');
    } catch {
      // sessionStorage unavailable (sandboxed/partitioned context): without the
      // one-shot guard we can't rule out a reload loop, so skip auto-recovery
      // rather than risk reloading repeatedly.
      return;
    }
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault(); // suppress Vite's throw; we handle recovery
    recoverFromStaleChunk();
  });

  router.onError((error) => {
    if (STALE_CHUNK_ERROR_RE.test(error?.message ?? '')) {
      recoverFromStaleChunk();
    }
  });

  router.afterEach(() => {
    try {
      sessionStorage.removeItem(STALE_CHUNK_KEY);
    } catch {
      // ignore — storage unavailable
    }
  });

  app.mount('#app');
}

bootstrap();
